import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { supabase } from '../supabase';
import { speakText } from '../utils/speak';
import { useUserLanguage } from '../i18n/UserLanguageContext';
import { useLanguage } from '../i18n/LanguageContext';
import { speakWithLuna, stopLunaVoice } from '../utils/lunaVoice';
import { awardXP } from '../utils/xp';
import './StoryReader.css';
import LunaLoader from '../LunaLoader';

const TEXT = {
    en: {
        loading: 'Loading...',
        preparing: 'Preparing your story...',
        story: 'Story',
        cantLoad: 'This story couldn’t load right now. Please try again shortly.',
        complete: 'Story complete!',
        correct: 'correct',
        back: 'Back to Stories',
        question: 'Question',
        finish: 'Finish →',
        next: 'Next →',
        choose: 'Choose an answer',
        lunaReads: '🔊 Gabi reads',
        lunaStop: '⏹ Gabi',
        speed: 'Voice speed',
        readAloud: '🎤 Read aloud',
        stopRecording: '⏹ Stop recording',
        checking: 'Checking...',
        recordingHint: '🔴 Recording: read the paragraph aloud, then tap "Stop recording"',
        micNeeded: 'Microphone access is needed for Read aloud mode.',
        noHearTitle: 'Couldn’t hear that clearly',
        noHearSub: 'Try recording again, a bit closer to the microphone.',
        tryAgain: '🎤 Try again',
        wentWrong: 'Something went wrong',
        wordsClear: 'words pronounced clearly',
        great: 'Great reading! Every word came through clearly.',
        redWords: 'Words in red weren’t recognized clearly. Say them slowly and try again.',
        legendClear: '✓ Clear',
        legendMiss: '✕ Try again',
        readAgain: '🎤 Read again',
        test: 'Test your understanding →',
        hear: '🔊 Hear it',
        saved: '✓ Saved to your Word Bank!',
        save: '🔖 Save to Word Bank',
        close: 'Close',
        questionsFailed: 'The questions couldn’t load right now.',
        retry: 'Try again',
        noTranslation: 'Translation unavailable.',
    },
    it: {
        loading: 'Caricamento...',
        preparing: 'Sto preparando la tua storia...',
        story: 'Storia',
        cantLoad: 'Impossibile caricare questa storia al momento. Riprova tra poco.',
        complete: 'Storia completata!',
        correct: 'corrette',
        back: 'Torna alle storie',
        question: 'Domanda',
        finish: 'Fine →',
        next: 'Avanti →',
        choose: 'Scegli una risposta',
        lunaReads: '🔊 Gabi legge',
        lunaStop: '⏹ Gabi',
        speed: 'Velocità della voce',
        readAloud: '🎤 Leggi ad alta voce',
        stopRecording: '⏹ Ferma la registrazione',
        checking: 'Controllo...',
        recordingHint: '🔴 Registrazione in corso: leggi il paragrafo ad alta voce, poi tocca "Ferma la registrazione"',
        micNeeded: 'Serve l’accesso al microfono per la lettura ad alta voce.',
        noHearTitle: 'Non ho sentito bene',
        noHearSub: 'Prova a registrare di nuovo, un po’ più vicino al microfono.',
        tryAgain: '🎤 Riprova',
        wentWrong: 'Qualcosa è andato storto',
        wordsClear: 'parole pronunciate in modo chiaro',
        great: 'Ottima lettura! Ogni parola è stata chiara.',
        redWords: 'Le parole in rosso non sono state riconosciute bene. Dille lentamente e riprova.',
        legendClear: '✓ Chiaro',
        legendMiss: '✕ Riprova',
        readAgain: '🎤 Leggi di nuovo',
        test: 'Metti alla prova la comprensione →',
        hear: '🔊 Ascolta',
        saved: '✓ Salvata nel tuo Word Bank!',
        save: '🔖 Salva nel Word Bank',
        close: 'Chiudi',
        questionsFailed: 'Non è stato possibile caricare le domande al momento.',
        retry: 'Riprova',
        noTranslation: 'Traduzione non disponibile.',
    },
    es: {
        loading: 'Cargando...',
        preparing: 'Preparando tu historia...',
        story: 'Historia',
        cantLoad: 'Esta historia no se pudo cargar ahora. Inténtalo de nuevo en un momento.',
        complete: '¡Historia completada!',
        correct: 'correctas',
        back: 'Volver a las historias',
        question: 'Pregunta',
        finish: 'Terminar →',
        next: 'Siguiente →',
        choose: 'Elige una respuesta',
        lunaReads: '🔊 Gabi lee',
        lunaStop: '⏹ Gabi',
        speed: 'Velocidad de la voz',
        readAloud: '🎤 Leer en voz alta',
        stopRecording: '⏹ Parar la grabación',
        checking: 'Comprobando...',
        recordingHint: '🔴 Grabando: lee el párrafo en voz alta y luego toca "Parar la grabación"',
        micNeeded: 'Se necesita acceso al micrófono para leer en voz alta.',
        noHearTitle: 'No se oyó con claridad',
        noHearSub: 'Intenta grabar de nuevo, un poco más cerca del micrófono.',
        tryAgain: '🎤 Intentar de nuevo',
        wentWrong: 'Algo salió mal',
        wordsClear: 'palabras pronunciadas con claridad',
        great: '¡Gran lectura! Todas las palabras se oyeron con claridad.',
        redWords: 'Las palabras en rojo no se reconocieron bien. Dilas despacio e inténtalo de nuevo.',
        legendClear: '✓ Claro',
        legendMiss: '✕ Otra vez',
        readAgain: '🎤 Leer otra vez',
        test: 'Pon a prueba tu comprensión →',
        hear: '🔊 Escuchar',
        saved: '✓ ¡Guardada en tu Word Bank!',
        save: '🔖 Guardar en el Word Bank',
        close: 'Cerrar',
        questionsFailed: 'Las preguntas no se pudieron cargar ahora.',
        retry: 'Intentar de nuevo',
        noTranslation: 'Traducción no disponible.',
    },
    fr: {
        loading: 'Chargement...',
        preparing: 'Préparation de ton histoire...',
        story: 'Histoire',
        cantLoad: 'Cette histoire n’a pas pu se charger pour l’instant. Réessaie dans un moment.',
        complete: 'Histoire terminée !',
        correct: 'correctes',
        back: 'Retour aux histoires',
        question: 'Question',
        finish: 'Terminer →',
        next: 'Suivant →',
        choose: 'Choisis une réponse',
        lunaReads: '🔊 Gabi lit',
        lunaStop: '⏹ Gabi',
        speed: 'Vitesse de la voix',
        readAloud: '🎤 Lire à voix haute',
        stopRecording: '⏹ Arrêter l’enregistrement',
        checking: 'Vérification...',
        recordingHint: '🔴 Enregistrement : lis le paragraphe à voix haute, puis touche "Arrêter l’enregistrement"',
        micNeeded: 'L’accès au micro est nécessaire pour la lecture à voix haute.',
        noHearTitle: 'Je n’ai pas bien entendu',
        noHearSub: 'Essaie de t’enregistrer à nouveau, un peu plus près du micro.',
        tryAgain: '🎤 Réessayer',
        wentWrong: 'Une erreur s’est produite',
        wordsClear: 'mots prononcés clairement',
        great: 'Belle lecture ! Chaque mot a été clair.',
        redWords: 'Les mots en rouge n’ont pas été bien reconnus. Dis-les lentement et réessaie.',
        legendClear: '✓ Clair',
        legendMiss: '✕ À refaire',
        readAgain: '🎤 Relire',
        test: 'Teste ta compréhension →',
        hear: '🔊 Écouter',
        saved: '✓ Enregistré dans ton Word Bank !',
        save: '🔖 Ajouter au Word Bank',
        close: 'Fermer',
        questionsFailed: 'Les questions n’ont pas pu se charger pour l’instant.',
        retry: 'Réessayer',
        noTranslation: 'Traduction indisponible.',
    },
    pt: {
        loading: 'Carregando...',
        preparing: 'Preparando sua história...',
        story: 'História',
        cantLoad: 'Esta história não pôde ser carregada agora. Tente novamente em instantes.',
        complete: 'História concluída!',
        correct: 'corretas',
        back: 'Voltar às histórias',
        question: 'Pergunta',
        finish: 'Concluir →',
        next: 'Próxima →',
        choose: 'Escolha uma resposta',
        lunaReads: '🔊 Gabi lê',
        lunaStop: '⏹ Gabi',
        speed: 'Velocidade da voz',
        readAloud: '🎤 Ler em voz alta',
        stopRecording: '⏹ Parar a gravação',
        checking: 'Verificando...',
        recordingHint: '🔴 Gravando: leia o parágrafo em voz alta e depois toque em "Parar a gravação"',
        micNeeded: 'É preciso acesso ao microfone para ler em voz alta.',
        noHearTitle: 'Não ouvi com clareza',
        noHearSub: 'Tente gravar de novo, um pouco mais perto do microfone.',
        tryAgain: '🎤 Tentar de novo',
        wentWrong: 'Algo deu errado',
        wordsClear: 'palavras pronunciadas com clareza',
        great: 'Ótima leitura! Todas as palavras ficaram claras.',
        redWords: 'As palavras em vermelho não foram reconhecidas bem. Diga-as devagar e tente de novo.',
        legendClear: '✓ Claro',
        legendMiss: '✕ De novo',
        readAgain: '🎤 Ler de novo',
        test: 'Teste sua compreensão →',
        hear: '🔊 Ouvir',
        saved: '✓ Salva no seu Word Bank!',
        save: '🔖 Salvar no Word Bank',
        close: 'Fechar',
        questionsFailed: 'As perguntas não puderam ser carregadas agora.',
        retry: 'Tentar de novo',
        noTranslation: 'Tradução indisponível.',
    },
};

export default function StoryReader() {
    const navigate = useNavigate();
    const { storySlug } = useParams();
    const location = useLocation();
    const { title, brief, isClassic, displayTitle } = location.state || {};
    const [readingIdx, setReadingIdx] = useState(null);
    const [voiceSpeed, setVoiceSpeed] = useState(() => {
        const lvl = localStorage.getItem('vozoly_level') || 'A1';
        return lvl === 'A1' ? 0.8 : lvl === 'A2' ? 0.9 : 1.0;
    });

    const changeSpeed = () => {
        const speeds = [0.7, 0.8, 0.9, 1.0];
        setVoiceSpeed(prev => speeds[(speeds.indexOf(prev) + 1) % speeds.length]);
    };

    useEffect(() => {
        return () => {
            stopLunaVoice();
            try { mediaRecorderRef.current?.stop(); } catch (e) { }
            streamRef.current?.getTracks().forEach(t => t.stop());
        };
    }, []);

    const [story, setStory] = useState(null);
    const [loading, setLoading] = useState(true);
    const [errored, setErrored] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);
    const [phase, setPhase] = useState('reading');
    const [currentQ, setCurrentQ] = useState(0);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [showResult, setShowResult] = useState(false);
    const [correctCount, setCorrectCount] = useState(0);
    const [saveWord, setSaveWord] = useState(null);
    const [wordSaved, setWordSaved] = useState(false);
    const [translations, setTranslations] = useState({});
    const [translatingIdx, setTranslatingIdx] = useState(null);
    const [recordingIdx, setRecordingIdx] = useState(null);
    const [processingIdx, setProcessingIdx] = useState(null);
    const [readAloudResults, setReadAloudResults] = useState({});
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const streamRef = useRef(null);

    const { activeLanguage, activeLevel } = useUserLanguage();
    const level = activeLevel || localStorage.getItem('vozoly_level') || 'A1';
    const targetLang = (activeLanguage || localStorage.getItem('vozoly_target_language') || 'italian').toLowerCase();
    const targetLangName = targetLang.charAt(0).toUpperCase() + targetLang.slice(1);
    const { interfaceLanguage } = useLanguage();
    const uiLang = TEXT[interfaceLanguage] ? interfaceLanguage : 'en';
    const s = (key) => TEXT[uiLang][key] || TEXT.en[key];

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            setErrored(false);
            try {
                const response = await fetch('/api/generate-story', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        storySlug,
                        storyTitle: title || storySlug.replace(/-/g, ' '),
                        storyBrief: brief,
                        isClassic: !!isClassic,
                        language: targetLang,
                        level,
                        interfaceLanguage,
                    }),
                });
                if (!response.ok) throw new Error('failed');
                const data = await response.json();
                setStory(data.content);
            } catch (err) {
                setErrored(true);
            }
            setLoading(false);
        };
        load();
    }, [storySlug, title, brief, isClassic, level, targetLang, interfaceLanguage, reloadKey]);

    const handleWordTap = (rawWord) => {
        const cleaned = rawWord.replace(/[.,!?;:""''()]/g, '').trim().toLowerCase();
        if (cleaned.length < 2) return;
        setSaveWord(cleaned);
        setWordSaved(false);
    };

    const saveWordToBank = async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user || !saveWord) return;
        await supabase.from('word_bank').upsert({
            user_id: user.id,
            word: saveWord,
            source: 'story',
            entry_type: 'word',
            target_language: targetLang,
        }, { onConflict: 'user_id,word,target_language', ignoreDuplicates: true });
        setWordSaved(true);
        fetch('/api/identify-word', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ word: saveWord, language: targetLangName, interfaceLanguage: uiLang }),
        })
            .then(r => r.json())
            .then(async result => {
                if (!result.valid) return;
                await supabase.from('word_bank').upsert({
                    user_id: user.id,
                    word: saveWord,
                    translation: result.translation,
                    phonetic: result.phonetic,
                    type: result.type,
                    note: result.note || '',
                    examples: result.examples || [],
                    entry_type: 'word',
                    source: 'story',
                    target_language: targetLang,
                }, { onConflict: 'user_id,word,target_language' });
            })
            .catch(() => { });
    };

    const normalizeWord = (w) => w.toLowerCase().replace(/[.,!?;:""''()]/g, '');

    const compareTranscript = (original, spoken) => {
        const originalWords = original.split(/\s+/);
        const spokenWords = new Set(spoken.split(/\s+/).map(normalizeWord));
        return originalWords.map(w => ({
            word: w,
            matched: spokenWords.has(normalizeWord(w)),
        }));
    };

    const startRecording = async (idx) => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;
            audioChunksRef.current = [];
            const recorder = new MediaRecorder(stream);
            recorder.ondataavailable = (e) => {
                if (e.data.size > 0) audioChunksRef.current.push(e.data);
            };
            mediaRecorderRef.current = recorder;
            recorder.start();
            setRecordingIdx(idx);
            setReadAloudResults(prev => ({ ...prev, [idx]: undefined }));
        } catch (err) {
            alert(TEXT[uiLang].micNeeded);
        }
    };

    const stopRecording = async (idx) => {
        setRecordingIdx(null);
        const recorder = mediaRecorderRef.current;
        if (!recorder || recorder.state === 'inactive') return;

        const blob = await new Promise((resolve) => {
            recorder.onstop = () => {
                streamRef.current?.getTracks().forEach(t => t.stop());
                resolve(new Blob(audioChunksRef.current, { type: recorder.mimeType }));
            };
            recorder.stop();
        });

        if (blob.size < 1000) return;
        setProcessingIdx(idx);

        try {
            const base64 = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result.split(',')[1]);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });

            const createRes = await fetch('/api/transcribe', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ audio: base64, language: targetLang }),
            });
            if (!createRes.ok) throw new Error('failed');
            const { id } = await createRes.json();

            let text = '';
            for (let i = 0; i < 30; i++) {
                await new Promise(r => setTimeout(r, 1200));
                const pollRes = await fetch(`/api/transcribe?id=${id}`);
                const poll = await pollRes.json();
                if (poll.status === 'completed') { text = poll.text; break; }
                if (poll.status === 'error') break;
            }

            if (text.trim()) {
                const comparison = compareTranscript(story.paragraphs[idx], text);
                setReadAloudResults(prev => ({ ...prev, [idx]: comparison }));
            } else {
                setReadAloudResults(prev => ({ ...prev, [idx]: 'nohear' }));
            }
        } catch (err) {
            setReadAloudResults(prev => ({ ...prev, [idx]: 'error' }));
        }
        setProcessingIdx(null);
    };

    const translateParagraph = async (idx) => {
        if (translations[idx]) {
            setTranslations(prev => ({ ...prev, [idx]: undefined }));
            return;
        }
        setTranslatingIdx(idx);
        try {
            const response = await fetch('/api/luna-chat', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    messages: [{ role: 'user', content: story.paragraphs[idx] }],
                    language: targetLangName,
                    interfaceLanguage,
                    mode: 'translate',
                }),
            });
            const data = await response.json();
            setTranslations(prev => ({ ...prev, [idx]: data.reply }));
        } catch (err) {
            setTranslations(prev => ({ ...prev, [idx]: TEXT[uiLang].noTranslation }));
        }
        setTranslatingIdx(null);
    };

    const startQuestions = () => {
        setPhase('questions');
        setCurrentQ(0);
        setCorrectCount(0);
    };

    const handleAnswer = (index) => {
        if (showResult) return;
        setSelectedAnswer(index);
        setShowResult(true);
        if (index === story.questions[currentQ].correct) {
            setCorrectCount(c => c + 1);
        }
    };

    const nextQuestion = () => {
        if (currentQ + 1 >= story.questions.length) {
            setPhase('complete');
            awardXP(20 + correctCount * 5, 'story');
        } else {
            setCurrentQ(currentQ + 1);
            setSelectedAnswer(null);
            setShowResult(false);
        }
    };

    if (loading) {
        return (
            <div className="sr-screen">
                <div className="sr-top-bar">
                    <button className="sr-back" onClick={() => { stopLunaVoice(); navigate(-1); }}>✕</button>
                    <div className="sr-title">{s('loading')}</div>
                    <div style={{ width: 32 }}></div>
                </div>
                <LunaLoader kind="story" />
            </div>
        );
    }

    if (errored || !story) {
        return (
            <div className="sr-screen">
                <div className="sr-top-bar">
                    <button className="sr-back" onClick={() => { stopLunaVoice(); navigate(-1); }}>✕</button>
                    <div className="sr-title">{s('story')}</div>
                    <div style={{ width: 32 }}></div>
                </div>
                <div className="sr-state">{s('cantLoad')}</div>
            </div>
        );
    }

    if (phase === 'complete') {
        return (
            <div className="sr-screen">
                <div className="sr-complete">
                    <div className="sr-complete-icon">📖</div>
                    <div className="sr-complete-title">{s('complete')}</div>
                    <div className="sr-complete-stat">{correctCount} / {story.questions.length} {s('correct')}</div>
                    <div className="sr-complete-xp">+{20 + correctCount * 5} XP</div>
                    <button className="sr-back-btn" onClick={() => navigate('/stories')}>{s('back')}</button>
                </div>
            </div>
        );
    }

    if (phase === 'questions') {
        const q = story.questions[currentQ];
        return (
            <div className="sr-screen">
                <div className="sr-top-bar">
                    <button className="sr-back" onClick={() => { stopLunaVoice(); navigate(-1); }}>✕</button>
                    <div className="sr-title">{s('question')} {currentQ + 1} / {story.questions.length}</div>
                    <div style={{ width: 32 }}></div>
                </div>
                <div className="sr-question">
                    <div className="sr-question-text">{q.question}</div>
                    <div className="sr-options">
                        {q.options.map((opt, i) => {
                            let cls = 'sr-option';
                            if (showResult) {
                                if (i === q.correct) cls += ' correct';
                                else if (i === selectedAnswer) cls += ' wrong';
                                else cls += ' faded';
                            }
                            return (
                                <div key={i} className={cls} onClick={() => handleAnswer(i)}>
                                    {opt}
                                </div>
                            );
                        })}
                    </div>
                </div>
                <div className="sr-footer">
                    {showResult ? (
                        <button className="sr-btn" onClick={nextQuestion}>
                            {currentQ + 1 >= story.questions.length ? s('finish') : s('next')}
                        </button>
                    ) : (
                        <button className="sr-btn" disabled>{s('choose')}</button>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="sr-screen">
            <div className="sr-top-bar">
                <button className="sr-back" onClick={() => { stopLunaVoice(); navigate(-1); }}>✕</button>
                <div className="sr-title">{displayTitle || story.title}</div>
                <div className="sr-top-right">
                    <button className="sr-speed-btn" onClick={changeSpeed} title={s('speed')}>{voiceSpeed}x</button>
                    <div className="sr-level-badge">{story.level}</div>
                </div>
            </div>

            <div className="sr-story-body">
                {story.paragraphs.map((p, i) => {
                    const result = readAloudResults[i];
                    return (
                        <div key={i} className="sr-paragraph-block">
                            <div className="sr-paragraph">
                                {Array.isArray(result) ? (
                                    result.map((w, k) => (
                                        <span key={k} className={w.matched ? 'sr-word-ok' : 'sr-word-miss'}>
                                            {w.word}{' '}
                                        </span>
                                    ))
                                ) : (
                                    p.split(/(\s+)/).map((part, j) =>
                                        /\S/.test(part) ? (
                                            <span key={j} className="sr-word" onClick={() => handleWordTap(part)}>{part}</span>
                                        ) : (
                                            <span key={j}>{part}</span>
                                        )
                                    )
                                )}
                            </div>
                            <div className="sr-paragraph-actions">
                                <button
                                    className="sr-mini-btn"
                                    onClick={() => {
                                        if (readingIdx === i) {
                                            stopLunaVoice();
                                            setReadingIdx(null);
                                        } else {
                                            setReadingIdx(i);
                                            speakWithLuna(p, () => setReadingIdx(null), voiceSpeed);
                                        }
                                    }}
                                >
                                    {readingIdx === i ? s('lunaStop') : s('lunaReads')}
                                </button>
                                <button className="sr-mini-btn" onClick={() => translateParagraph(i)}>
                                    {translatingIdx === i ? '...' : '文'}
                                </button>
                                {!Array.isArray(result) && result !== 'nohear' && result !== 'error' && (
                                    <button
                                        className={`sr-mini-btn ${recordingIdx === i ? 'recording' : ''}`}
                                        onClick={() => recordingIdx === i ? stopRecording(i) : startRecording(i)}
                                        disabled={processingIdx === i}
                                    >
                                        {processingIdx === i ? s('checking') : recordingIdx === i ? s('stopRecording') : s('readAloud')}
                                    </button>
                                )}
                            </div>
                            {recordingIdx === i && (
                                <div className="sr-recording-hint">{s('recordingHint')}</div>
                            )}
                            {translations[i] && (
                                <div className="sr-translation">{translations[i]}</div>
                            )}
                            {result === 'nohear' && (
                                <div className="sr-readaloud-card error">
                                    <div className="sr-readaloud-title">{s('noHearTitle')}</div>
                                    <div className="sr-readaloud-sub">{s('noHearSub')}</div>
                                    <button className="sr-mini-btn" onClick={() => startRecording(i)}>{s('tryAgain')}</button>
                                </div>
                            )}
                            {result === 'error' && (
                                <div className="sr-readaloud-card error">
                                    <div className="sr-readaloud-title">{s('wentWrong')}</div>
                                    <button className="sr-mini-btn" onClick={() => startRecording(i)}>{s('tryAgain')}</button>
                                </div>
                            )}
                            {Array.isArray(result) && (
                                <div className="sr-readaloud-card">
                                    <div className="sr-readaloud-title">
                                        {result.filter(w => w.matched).length} / {result.length} {s('wordsClear')}
                                    </div>
                                    <div className="sr-readaloud-sub">
                                        {result.every(w => w.matched) ? s('great') : s('redWords')}
                                    </div>
                                    <div className="sr-readaloud-legend">
                                        <span className="sr-legend-chip ok">{s('legendClear')}</span>
                                        <span className="sr-legend-chip miss">{s('legendMiss')}</span>
                                    </div>
                                    <button className="sr-mini-btn" onClick={() => { setReadAloudResults(prev => ({ ...prev, [i]: undefined })); startRecording(i); }}>
                                        {s('readAgain')}
                                    </button>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            <div className="sr-footer">
                {story.questionsFailed ? (
                    <>
                        <div style={{ fontSize: 13, color: '#6B7280', textAlign: 'center', marginBottom: 8 }}>{s('questionsFailed')}</div>
                        <button className="sr-btn" onClick={() => setReloadKey(k => k + 1)}>{s('retry')}</button>
                    </>
                ) : (
                    <button className="sr-btn" onClick={startQuestions}>{s('test')}</button>
                )}
            </div>

            {saveWord && (
                <div className="sr-save-overlay" onClick={() => setSaveWord(null)}>
                    <div className="sr-save-sheet" onClick={e => e.stopPropagation()}>
                        <div className="sr-save-word">{saveWord}</div>
                        <button className="sr-save-hear" onClick={() => speakText(saveWord, undefined, targetLang)}>{s('hear')}</button>
                        {wordSaved ? (
                            <div className="sr-save-done">{s('saved')}</div>
                        ) : (
                            <button className="sr-save-btn" onClick={saveWordToBank}>{s('save')}</button>
                        )}
                        <button className="sr-save-cancel" onClick={() => setSaveWord(null)}>{s('close')}</button>
                    </div>
                </div>
            )}
        </div>
    );
}